import { useState, useEffect, useCallback } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useActiveAnnouncements } from '@/hooks/useActiveAnnouncements'
import { useViewMode } from '@/stores/viewMode'
import { useAuthStore } from '@/stores/auth'
import { useMe } from '@/hooks/useMe'

function getUserIdFromToken(token: string | null): number | null {
  if (!token) return null
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.user_id ?? null
  } catch {
    return null
  }
}

function getTodayKey(mode: string, id: number, userId: number): string {
  const now = new Date()
  const date = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}-${String(now.getUTCDate()).padStart(2, '0')}`
  return `announcement_dismissed_${mode}_${id}_${userId}_${date}`
}

function isDismissedToday(mode: string, id: number, userId: number): boolean {
  const key = getTodayKey(mode, id, userId)
  return localStorage.getItem(key) === 'true'
}

function dismissToday(mode: string, id: number, userId: number): void {
  const key = getTodayKey(mode, id, userId)
  localStorage.setItem(key, 'true')
}

export default function AnnouncementDialog() {
  const { mode, id: viewModeId } = useViewMode()
  const token = useAuthStore((state) => state.token)
  const userId = getUserIdFromToken(token)
  const { user } = useMe()
  const { announcements, isLoading } = useActiveAnnouncements()
  const [open, setOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [dismissChecked, setDismissChecked] = useState(false)

  // Provider/sys_admin users should never see announcements
  // Announcements are only for clinic and ad_supplier level users
  const isAdminUser = user?.role_type === 'sys_admin' || user?.scopes?.provider_id != null

  // Check if should show dialog when announcements load
  useEffect(() => {
    if (isLoading || !announcements.length || !viewModeId || !userId) return
    if (isAdminUser) return
    if (isDismissedToday(mode, viewModeId, userId)) return

    setCurrentIndex(0)
    setDismissChecked(false)
    setOpen(true)
  }, [announcements, isLoading, mode, viewModeId, userId, isAdminUser])

  const handleClose = useCallback(() => {
    if (dismissChecked && viewModeId && userId) {
      dismissToday(mode, viewModeId, userId)
    }
    setOpen(false)
  }, [dismissChecked, mode, viewModeId, userId])

  const handlePrev = () => {
    setCurrentIndex((i) => Math.max(0, i - 1))
  }

  const handleNext = () => {
    setCurrentIndex((i) => Math.min(announcements.length - 1, i + 1))
  }

  if (!announcements.length) return null

  const current = announcements[currentIndex]
  const total = announcements.length
  const isFirst = currentIndex === 0
  const isLast = currentIndex === total - 1
  const hasMultiple = total > 1

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{current?.title}</DialogTitle>
        </DialogHeader>

        <div className="py-2 whitespace-pre-wrap text-sm text-muted-foreground min-h-[100px] max-h-[300px] overflow-y-auto">
          {current?.content}
        </div>

        <DialogFooter className="flex !justify-between items-center">
          <div className="flex items-center gap-2">
            <Checkbox
              id="dismiss-today"
              checked={dismissChecked}
              onCheckedChange={(checked) => setDismissChecked(checked === true)}
            />
            <label
              htmlFor="dismiss-today"
              className="text-sm text-muted-foreground cursor-pointer select-none"
            >
              今日不再顯示
            </label>
          </div>

          {hasMultiple ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={isFirst}
                onClick={handlePrev}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground min-w-[40px] text-center">
                {currentIndex + 1} / {total}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={isLast}
                onClick={handleNext}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
